import axiosInstance from '../../axios/axiosInstance'

export type OnboardingLessonStatus = 'available' | 'coming_soon'

export interface OnboardingWord {
  word: string
  pinyin: string
  english: string
}

export interface OnboardingLesson {
  id: string
  order: number
  title: string
  subtitle: string
  status: OnboardingLessonStatus
  youtubeId: string | null
  wordCount: number
  watched: boolean
  /** Vocabulary for available lessons (empty for coming-soon). */
  words: OnboardingWord[]
}

export interface OnboardingSeriesMeta {
  languageId: string
  level: string
  title: string
  description: string
  targetWordCount: number
  textbook: {
    title: string
    titleZh?: string
    publisher: string
    note: string
    coverUrl?: string
    /** Partner link carrying the maayot discount code. */
    purchaseUrl?: string
    /** Whole-number percentage off via `purchaseUrl`; 0/absent hides the offer. */
    discountPercent?: number
  }
}

export interface OnboardingSeriesResponse {
  eligible: boolean
  dismissed: boolean
  series: OnboardingSeriesMeta
  lessons: OnboardingLesson[]
}

export interface OnboardingWatchedWord {
  word: string
  pinyin: string
  english: string
}

export interface OnboardingWatchedResponse {
  lessonId: string
  alreadyWatched: boolean
  markedCount: number
  words: OnboardingWatchedWord[]
}

/**
 * Fetches the HSK 1 onboarding video series for the current learner. Returns
 * null on any failure so the /stories page degrades silently (the section just
 * doesn't render) rather than breaking the dashboard.
 */
export const getOnboardingVideos = async (): Promise<OnboardingSeriesResponse | null> => {
  try {
    const res = await axiosInstance.get('onboarding-videos')
    return (res?.data?.data as OnboardingSeriesResponse) ?? null
  } catch {
    return null
  }
}

/** Marks a lesson watched and moves its words into the learner's "Learning" list. */
export const markOnboardingVideoWatched = async (lessonId: string): Promise<OnboardingWatchedResponse> => {
  const res = await axiosInstance.post(`onboarding-videos/${lessonId}/watched`)
  return res?.data?.data as OnboardingWatchedResponse
}

/** Permanently hides the series for this learner. */
export const dismissOnboardingVideos = async (): Promise<void> => {
  await axiosInstance.post('onboarding-videos/dismiss')
}
